import { useTranslation } from 'react-i18next';

import { formatPhoneForDisplay } from '@/shared/utils/phone';

import { useAdminEditStore } from '../store/admin-edit.store';
import type { Admin } from '../types/admin.types';
import { useState } from 'react';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import { useResendInvitation, useResetPassword } from '../hooks/use-admin-mutations';
import { AdminApiError } from '../types/admin.types';
import { formatLastLogin } from '../utils/format-date';
import { AdminStatusBadge } from './AdminStatusBadge';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

interface AdminListRowProps {
  admin: Admin;
}

export function AdminListRow({ admin }: AdminListRowProps) {
  const { t } = useTranslation('admin');
  const { canWrite, denyProps, tooltip } = useWriteGuard('SYSTEM_MGMT');

  // What the server says about the email and phone it just sent for this row, rather than an inference from
  // the status: a withdrawn row with no archive row to draw on sends nothing to mark. Falls back to the
  // status so the chip still appears against a server that predates the flag.
  const archivedIdentity = admin.identityArchived ?? admin.status === 'WITHDRAWN';

  return (
    <tr>
      <td>{admin.id}</td>
      <td>
        <div style={{ fontWeight: 500, lineHeight: 1.4 }}>
          {admin.email}
          {/* A withdrawn account released its email and phone so the addresses could be used again; what
              the row shows is the pair archived at withdrawal time. Chipping it keeps the address itself
              readable and copyable while saying plainly that the account no longer holds it. */}
          {archivedIdentity && (
            <span
              className="badge badge-secondary"
              style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, verticalAlign: 'middle' }}
              title={t('adminManagement.editPanel.archivedIdentityChipTooltip')}
            >
              {t('adminManagement.editPanel.archivedIdentityChip')}
            </span>
          )}
        </div>
        {admin.phone && (
          <div
            style={{ color: 'var(--muted-foreground)', fontSize: 12, lineHeight: 1.4 }}
          >
            {formatPhoneForDisplay(admin.phone)}
          </div>
        )}
      </td>
      <td>{admin.name}</td>
      <td>
        {admin.isRoot ? (
          <span
            className="badge badge-destructive"
            style={{ fontWeight: 600 }}
            title={t('adminManagement.list.rootBadge')}
          >
            👑 {t('adminManagement.list.rootBadge')}
          </span>
        ) : admin.roles.length > 0 ? (
          <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {admin.roles.map((r) => (
              <span key={r} className="badge badge-outline">
                {r}
              </span>
            ))}
          </div>
        ) : (
          <span style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>
            {t('adminManagement.list.noRoles')}
          </span>
        )}
      </td>
      <td>
        <AdminStatusBadge status={admin.status} />
      </td>
      <td>{formatLastLogin(admin.lastLoginAt)}</td>
      <td>
        <PencilButton admin={admin} canWrite={canWrite} />
      </td>
      <td>
        {admin.note ? (
          admin.note
        ) : (
          <span style={{ color: 'var(--muted-foreground)' }}>-</span>
        )}
      </td>
    </tr>
  );
}

interface PencilButtonProps {
  admin: Admin;
  canWrite: boolean;
}

// PRD 15 §4.1 (post-2026-05-26 update): management column collapses to a single
// pencil-edit button; all per-status actions (Resend / Reset Password / Withdraw)
// move into the edit panel itself.
//
// SHCT-691 — the Root row is the one exception, and it is a hide, not a disable.
// §3.3 line 161, §4.1 No.13 and the §8.3 exception table all say the same thing:
// 관리 컬럼에 [수정] 버튼 미노출 + "관리자 계정은 이양 후 편집 가능" 안내 텍스트만 표시.
// It used to render a dimmed ✏️ carrying that sentence in `title`/`aria-label`
// only, so the cell's visible text was the emoji and the reason surfaced solely
// on hover. Server-side blocking (403 ROOT_NOT_EDITABLE) was never at issue.
//
// This does not contradict SHCT-690's "never hide a permission-gated control":
// permission is a different axis, and no grant makes the Root row editable — the
// route is 이양 (§3.5), which the sentence names.
function PencilButton({ admin, canWrite }: PencilButtonProps) {
  const { t } = useTranslation('admin');
  const openEdit = useAdminEditStore((s) => s.open);

  if (admin.isRoot) {
    return (
      <span style={{ color: 'var(--muted-foreground)', fontSize: 12, lineHeight: 1.4 }}>
        {t('adminManagement.list.rootEditNote')}
      </span>
    );
  }

  // SHCT-690 — PRD 15 §1.2 / §7 grant SYSTEM_MGMT:READ "목록·상세 조회". Gating this
  // button on WRITE blocked the read too, because the detail lives behind it: the panel
  // is the only way into §3.6's per-status actions, so a READ user could see neither the
  // detail nor the disabled-action states the spec describes. The button now opens for
  // READ and the panel disables every write control instead (permission enforcement moves
  // one level in, where it belongs).
  const title = canWrite
    ? t('adminManagement.actions.edit')
    : t('adminManagement.actions.view');

  return (
    <button
      type="button"
      className="btn btn-default btn-secondary"
      style={{ height: 28, padding: '0 8px', fontSize: 14, lineHeight: 1 }}
      title={title}
      aria-label={title}
      onClick={() => openEdit(admin)}
    >
      ✏️
    </button>
  );
}
